"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef, useState } from "react";
import Image from "next/image";
import { DESKTOP_QUERY } from "@/lib/breakpoints";
const DESKTOP_LOAD_DURATION = 2000;
const MAX_LOAD_WAIT = 3000;
const REVEAL_DURATION = 450;
const MOBILE_LOAD_DURATION = 1200;
const MOBILE_TILE_COUNT = 64;
const TILE_COUNT = 144;
function createTileColors() {
  const colors = [...Array<string>(100).fill("bg-neutral-950 dark:bg-white"), ...Array<string>(100).fill("bg-neutral-900 dark:bg-slate-50")];
  let seed = 0x45d9f3b;
  for (let index = colors.length - 1; index > 0; index -= 1) {
    seed = Math.imul(seed ^ seed >>> 16, 2246822507);
    seed = Math.imul(seed ^ seed >>> 13, 3266489909);
    const target = (seed >>> 0) % (index + 1);
    [colors[index], colors[target]] = [colors[target], colors[index]];
  }
  return colors;
}
const tileColors = createTileColors();
function createTileDelays(count: number) {
  const order = Array.from({
    length: count
  }, (_, index) => index);
  const delays = Array<number>(count);
  let seed = 0x6d2b79f5;
  const random = () => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return (seed >>> 0) / 4294967296;
  };
  for (let index = order.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [order[index], order[target]] = [order[target], order[index]];
  }
  order.forEach((tileIndex, position) => {
    delays[tileIndex] = 60 + position * 4;
  });
  return delays;
}
const tileDelays = createTileDelays(TILE_COUNT);
export default function Preloader() {
  const $ = _c(16);
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(false);
  const logoFillRef = useRef(null);
  const progressTextRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsMobile(!window.matchMedia(DESKTOP_QUERY).matches);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useLayoutEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (window.sessionStorage.getItem("dezine-skip-preloader-once") === "true") {
        window.sessionStorage.removeItem("dezine-skip-preloader-once");
        setLoading(false);
        return;
      }
      const simpleLoader = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (simpleLoader) {
        setLoading(false);
        return;
      }
      let animationFrame = 0;
      let revealTimer = 0;
      let restored = false;
      const startedAt = performance.now();
      const loadDuration = window.matchMedia(DESKTOP_QUERY).matches ? DESKTOP_LOAD_DURATION : MOBILE_LOAD_DURATION;
      let lastProgress = -1;
      let pageLoaded = document.readyState === "complete";
      const onPageLoad = () => {
        pageLoaded = true;
      };
      if (!pageLoaded) {
        window.addEventListener("load", onPageLoad, {
          once: true
        });
      }
      const blockedScrollKeys = new Set(["ArrowDown", "ArrowLeft", "ArrowRight", "ArrowUp", "End", "Home", "PageDown", "PageUp", " "]);
      const preventScroll = _temp;
      const preventKeyboardScroll = event_0 => {
        if (blockedScrollKeys.has(event_0.key)) {
          event_0.preventDefault();
        }
      };
      const restorePageScroll = () => {
        if (restored) {
          return;
        }
        restored = true;
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "instant" as ScrollBehavior
        });
        window.removeEventListener("wheel", preventScroll);
        window.removeEventListener("touchmove", preventScroll);
        window.removeEventListener("keydown", preventKeyboardScroll);
      };
      window.addEventListener("wheel", preventScroll, {
        passive: false
      });
      window.addEventListener("touchmove", preventScroll, {
        passive: false
      });
      window.addEventListener("keydown", preventKeyboardScroll);
      let lastUpdateTime = 0;
      const updateProgress = now => {
        if (now - lastUpdateTime < 32) {
          animationFrame = requestAnimationFrame(updateProgress);
          return;
        }
        lastUpdateTime = now;
        const elapsed = now - startedAt;
        const timeProgress = Math.floor(elapsed / loadDuration * 100);
        const nextProgress = Math.min(100, Math.max(0, timeProgress));
        const ready = pageLoaded || elapsed >= MAX_LOAD_WAIT;
        if (nextProgress >= 100 && !ready) {
          animationFrame = requestAnimationFrame(updateProgress);
          return;
        }
        if (nextProgress !== lastProgress) {
          lastProgress = nextProgress;
          const clip = `inset(${100 - nextProgress}% 0 0 0)`;
          const fill = logoFillRef.current;
          if (fill) {
            fill.style.clipPath = clip;
            fill.style.setProperty("-webkit-clip-path", clip);
          }
          if (progressTextRef.current) {
            progressTextRef.current.textContent = String(nextProgress);
          }
        }
        if (nextProgress < 100) {
          animationFrame = requestAnimationFrame(updateProgress);
          return;
        }
        setRevealing(true);
        revealTimer = window.setTimeout(() => {
          restorePageScroll();
          setLoading(false);
        }, REVEAL_DURATION);
      };
      animationFrame = requestAnimationFrame(updateProgress);
      return () => {
        window.removeEventListener("load", onPageLoad);
        cancelAnimationFrame(animationFrame);
        window.clearTimeout(revealTimer);
        restorePageScroll();
      };
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useLayoutEffect(t2, t3);
  if (!loading) {
    return null;
  }
  const t4 = `fixed inset-0 z-[9999] h-[100dvh] w-screen overflow-hidden text-white transition-opacity duration-300 ${revealing ? "pointer-events-none bg-transparent" : "touch-none bg-neutral-950"}`;
  const t5 = `absolute inset-0 transition-opacity duration-200 ${revealing ? "opacity-0" : "opacity-100"}`;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Image src="/images/header/darkmodelogo.png" alt="The Dezine" fill={true} priority={true} sizes="(max-width: 720px) 80vw, 576px" className="object-contain opacity-20" />;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute left-1/2 top-1/2 aspect-[1712/374] w-[min(80vw,36rem)] -translate-x-1/2 -translate-y-1/2 transform-gpu">{t6}<div ref={logoFillRef} className="preloader-logo absolute inset-0 will-change-[clip-path] transform-gpu" style={{
        clipPath: "inset(100% 0 0 0)",
        WebkitClipPath: "inset(100% 0 0 0)"
      }}><Image src="/images/header/darkmodelogo.png" alt="" fill={true} sizes="(max-width: 720px) 80vw, 576px" className="object-contain" /></div></div>;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute bottom-5 right-5 flex items-start font-bold leading-none select-none sm:bottom-8 sm:right-10" style={{
      fontFamily: "var(--font-poppins), system-ui, -apple-system, sans-serif"
    }}><span ref={progressTextRef} className="min-w-24 text-right text-6xl tabular-nums sm:min-w-32 sm:text-8xl lg:text-9xl">0</span><span className="mt-2 text-2xl sm:mt-3 sm:text-3xl">%</span></div>;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t5) {
    t9 = <div className={t5}>{t7}{t8}</div>;
    $[7] = t5;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== isMobile || $[10] !== revealing) {
    t10 = revealing ? <div aria-hidden={true} className="preloader-tile-grid pointer-events-none absolute inset-0 z-20">{Array.from({
        length: isMobile ? MOBILE_TILE_COUNT : TILE_COUNT
      }, _temp2)}</div> : null;
    $[9] = isMobile;
    $[10] = revealing;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t4 || $[14] !== t9) {
    t11 = <div key="site-preloader" data-site-preloader={true} aria-label="Loading The Dezine" aria-live="polite" role="status" className={t4}>{t9}{t10}</div>;
    $[12] = t10;
    $[13] = t4;
    $[14] = t9;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  return t11;
}
function _temp2(_, index) {
  return <span key={index} className={`preloader-tile ${tileColors[index]}`} style={{
    animationDelay: `${tileDelays[index]}ms`
  }} />;
}
function _temp(event) {
  if (event.cancelable) {
    event.preventDefault();
  }
}